import React from "react";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { Modal, Descriptions, Image, Row, Col, Divider, Typography, Table, Tag, Space, Empty, Spin, Alert } from "antd";
import { GET } from "../services";

/**
 * Modal รายละเอียดเคส "ตัวกลาง" — ทุกหน้าที่มีปุ่มดูรายละเอียดเคสใช้ตัวนี้ตัวเดียว
 *
 * ส่งแค่ caseId แล้วตัวนี้ดึงข้อมูลเคสครบทุกช่องจาก GET /api/v1/caseDetail/:id เอง
 * → ไม่ว่ารายการของหน้านั้นจะส่งช่องอะไรมา ทุกหน้าเห็นข้อมูลเหมือนกัน
 * (เดิมแต่ละหน้าก๊อป modal ไปแก้เอง + ส่งข้อมูลคนละชุด หน้ารายงานเลยไม่มีหลักฐาน Reject/Inform)
 *
 * props
 *   caseId   เลขเคส (Inputs.id) — ไม่มี = แถวนี้ยังไม่ผูกเคส (เช่น รายการ audit ที่ Not Match)
 *   record   แถวจากรายการของหน้า ใช้แสดงไปก่อนระหว่างรอโหลด / ถ้าโหลดไม่สำเร็จ
 *   footer   ปุ่มเฉพาะหน้า (Pass/Fail ฯลฯ) — ไม่ส่ง = ไม่มีแถบปุ่ม
 *   extra    ข้อมูลเฉพาะหน้า แสดงบนสุด (เช่น ข้อมูลจากชีต audit)
 */

const { Title, Text } = Typography;

// ── ป้ายสถานะ ──────────────────────────────────────────────────────────────
// RejectSuccess / InformSuccess = พนักงานกด Cancel ปิดเคสทิ้ง "ไม่ใช่" ทำสำเร็จ
const STATUS_META = {
  Waiting: { color: "blue", label: "รอทำ" },
  Processing: { color: "processing", label: "กำลังทำ" },
  Pending: { color: "gold", label: "พักเคส" },
  Success: { color: "green", label: "สำเร็จ" },
  Reject: { color: "red", label: "ตีกลับ" },
  RejectSuccess: { color: "volcano", label: "ปิดเคส (หลังตีกลับ)" },
  Inform: { color: "orange", label: "ขอข้อมูลเพิ่ม" },
  InformSuccess: { color: "purple", label: "ปิดเคส (หลังขอข้อมูล)" },
};

const LOG_META = {
  Input: { color: "default", label: "สร้างเคส" },
  "Assign Auto": { color: "blue", label: "ระบบจ่ายเคส" },
  AssignManual: { color: "blue", label: "หัวหน้าจ่ายเคส" },
  manager_send_case: { color: "blue", label: "ส่งเคสจาก manager" },
  Redistribute: { color: "blue", label: "ส่งต่อเคส" },
  Start: { color: "cyan", label: "เริ่มทำ" },
  Pending: { color: "gold", label: "พักเคส" },
  Finish: { color: "green", label: "ทำสำเร็จ" },
  Reject: { color: "red", label: "ตีกลับ" },
  RejectFix: { color: "geekblue", label: "แก้แล้วส่งกลับ" },
  Inform: { color: "orange", label: "ขอข้อมูลเพิ่ม" },
  InformFix: { color: "geekblue", label: "เติมข้อมูลส่งกลับ" },
  CancelReject: { color: "volcano", label: "ปิดเคส (Cancel Reject)" },
  CancelInform: { color: "purple", label: "ปิดเคส (Cancel Inform)" },
  SetUserId: { color: "default", label: "กรอก User ทีหลัง" },
};

const isClosedStatus = (s) => s === "RejectSuccess" || s === "InformSuccess";

// เวลาใน history_logs ที่ backend ส่งมาถูกจัดรูปเป็น "DD/MM/YYYY HH:mm:ss" แล้ว
// แปลงกลับเป็น Date เพื่อคำนวณช่วงเวลา (ทุกค่าอยู่ในเขตเวลาเดียวกัน ลบกันได้ตรง)
const parseLogDate = (s) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/.exec(String(s || ""));
  if (!m) return null;
  return new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5], +m[6]);
};

const formatDuration = (from, to) => {
  if (!from || !to) return null;
  let sec = Math.round((to - from) / 1000);
  if (sec < 0) return null;
  const d = Math.floor(sec / 86400); sec -= d * 86400;
  const h = Math.floor(sec / 3600); sec -= h * 3600;
  const m = Math.floor(sec / 60); sec -= m * 60;
  return [d && `${d} วัน`, h && `${h} ชม.`, m && `${m} นาที`, `${sec} วิ`].filter(Boolean).join(" ");
};

const textBlock = (value) => (
  <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "100%" }}>
    {value || "-"}
  </div>
);

export function CaseDetailsBody({ record }) {
  if (!record) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US");
  };

  const logs = (Array.isArray(record.history_logs) ? record.history_logs : []).map((l, i) => ({ ...l, _key: i }));
  const firstLog = (types) => logs.find((l) => types.includes(l.type)) || null;
  const lastLog = (types) => [...logs].reverse().find((l) => types.includes(l.type)) || null;
  const countLog = (type) => logs.filter((l) => l.type === type).length;

  const createdLog = firstLog(["Input"]);
  const startLog = firstLog(["Start"]);
  const finishLog = lastLog(["Finish"]);
  const rejectLog = lastLog(["Reject"]);
  const informLog = lastLog(["Inform"]);
  const cancelLog = lastLog(["CancelReject", "CancelInform"]);

  // เคสที่ปิดก่อนแก้บั๊ก (2026-10-12) ช่อง note ถูกเขียนทับเป็นค่าว่างตอนกด Cancel
  // → อ่าน note ตอนสร้างเคสจาก log "Input" มาแสดงคู่กัน ถ้าไม่ตรงกับ note ปัจจุบัน
  const originalNote = createdLog && createdLog.note && createdLog.note !== "-" ? createdLog.note : null;
  const closed = isClosedStatus(record.status);
  const showOriginalNote = originalNote && originalNote !== record.note;
  // เหตุผลที่ปิดเคสอยู่ในประวัติ (log CancelReject/CancelInform) — ค่า default แปลว่าไม่ได้พิมพ์อะไรไว้
  const cancelReason =
    cancelLog && cancelLog.note && !["cancel reject", "cancel inform"].includes(cancelLog.note) ? cancelLog.note : null;

  const createdAt = parseLogDate(createdLog?.date);
  const startAt = parseLogDate(startLog?.date);
  const endLog = finishLog || cancelLog;
  const endAt = parseLogDate(endLog?.date);

  const statusMeta = STATUS_META[record.status] || { color: "default", label: record.status };

  // แสดงตามข้อมูลที่มี ไม่ผูกกับสถานะปัจจุบัน — เดิมแสดงเฉพาะตอนสถานะเป็น Reject/Inform พอดี
  // เคสที่ปิดไปแล้ว (RejectSuccess) หรือแก้แล้วส่งกลับ หลักฐานจึงหายจากหน้านี้ทั้งที่ยังอยู่ใน DB
  const hasReject = Boolean(record.reject_remark) || (record.imgReject || []).length > 0 || countLog("Reject") > 0;
  const hasInform = Boolean(record.inform_remark) || (record.imgInform || []).length > 0 || countLog("Inform") > 0;

  const renderImages = (images, title) => {
    if (!images || !Array.isArray(images) || images.length === 0) {
      return null;
    }

    return (
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>{title}</Title>
        <Row gutter={[16, 16]}>
          <Image.PreviewGroup>
            {images.map((image, index) => (
              <Col key={index} xs={12} sm={8} md={6}>
                <Image
                  width="100%"
                  src={image.img_url}
                  alt={`${title} ${index + 1}`}
                  style={{ borderRadius: 4 }}
                />
              </Col>
            ))}
          </Image.PreviewGroup>
        </Row>
      </div>
    );
  };

  const who = (log) => (log ? `${log.by || "-"} · ${log.date || "-"}` : "-");

  const renderCustomFields = (customFields) => {
    if (!customFields) return null;

    // รองรับทั้ง array format (ใหม่) และ object format (เก่า)
    let entries = [];
    
    if (Array.isArray(customFields)) {
      // Format ใหม่: array ของ objects [{number, title, value}, ...]
      // เรียงลำดับ: NOTE. ขึ้นก่อน แล้วเรียงตาม number
      const sortedFields = [...customFields].sort((a, b) => {
        const aIsNote = a.title === "NOTE." || a.title === "NOTE";
        const bIsNote = b.title === "NOTE." || b.title === "NOTE";
        
        // NOTE. ขึ้นก่อน
        if (aIsNote && !bIsNote) return -1;
        if (!aIsNote && bIsNote) return 1;
        if (aIsNote && bIsNote) return 0;
        
        // ถ้าไม่ใช่ NOTE. ให้เรียงตาม number
        return (a.number || 0) - (b.number || 0);
      });
      entries = sortedFields.map(field => [field.title, field.value]);
    } else if (typeof customFields === 'object') {
      // Format เก่า: object {title: value, ...}
      // เรียงลำดับ: NOTE. ขึ้นก่อน
      const objectEntries = Object.entries(customFields);
      entries = objectEntries.sort(([aTitle], [bTitle]) => {
        const aIsNote = aTitle === "NOTE." || aTitle === "NOTE";
        const bIsNote = bTitle === "NOTE." || bTitle === "NOTE";
        
        if (aIsNote && !bIsNote) return -1;
        if (!aIsNote && bIsNote) return 1;
        return 0;
      });
    } else {
      return null;
    }

    if (entries.length === 0) return null;

    // สร้าง columns สำหรับตาราง
    const columns = entries.map(([key, value]) => ({
      title: key,
      dataIndex: key,
      key: key,
      width: 200,
      render: (text, record) => {
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string' && value[0].startsWith('http')) {
          // Handle image URLs
          return (
            <Row gutter={[4, 4]}>
              {value.map((url, index) => (
                <Col key={index} span={12}>
                  <Image
                    height="100px"
                    width="200%"
                    src={url}
                    alt={`${key} ${index + 1}`}
                    style={{ borderRadius: 4 , height: "100%" }}
                  />
                </Col>
              ))}
            </Row>
          );
        } 
        else {
          // Handle text values
          return (
            <div
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxWidth: '100%',
                color: '#00910c',
                fontWeight: 'bold'
              }}
            >
              {value || "-"}
            </div>
          );
        }
      }
    }));

    // สร้าง data สำหรับตาราง (มีแค่ 1 แถว)
    const data = [{
      key: 'custom-fields',
      ...Object.fromEntries(entries)
    }];

    return (
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        size="small"
        bordered
        style={{ marginBottom: 16 }}
      />
    );
  };

  const historyColumns = [
    { title: "#", dataIndex: "No", key: "No", width: 50, render: (v, _, i) => v || i + 1 },
    { title: "เวลา", dataIndex: "date", key: "date", width: 160 },
    {
      title: "ประเภท",
      dataIndex: "type",
      key: "type",
      width: 170,
      render: (type) => {
        const meta = LOG_META[type] || { color: "default", label: type };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "รายละเอียด",
      key: "note",
      render: (_, log) => (
        <div>
          {log.note ? textBlock(log.note) : null}
          {Array.isArray(log.media) && log.media.length > 0 && (
            <Space wrap size={6} style={{ marginTop: log.note ? 6 : 0 }}>
              <Image.PreviewGroup>
                {log.media.map((url, i) => (
                  <Image key={i} src={url} width={56} height={56} style={{ objectFit: "cover", borderRadius: 4 }} />
                ))}
              </Image.PreviewGroup>
            </Space>
          )}
        </div>
      ),
    },
    { title: "โดย", dataIndex: "by", key: "by", width: 110 },
  ];

  return (
    <>
      {/* ── ข้อมูลเคส ── */}
      <Title level={4}>ข้อมูลเคส</Title>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="เลขเคส">{record.id}</Descriptions.Item>
        <Descriptions.Item label="สถานะ">
          <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.status}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="ประเภท">{record.caseName || "-"}</Descriptions.Item>
        <Descriptions.Item label="User ID">
          {record.userId || "-"} {record.isVip || record.isVIP ? <Tag color="gold">VIP</Tag> : null}
        </Descriptions.Item>
        <Descriptions.Item label="ยอด">{record.amount != null && record.amount !== "" ? record.amount : "-"}</Descriptions.Item>
        <Descriptions.Item label="Remark Code">{record.RemarkCode || "-"}</Descriptions.Item>
        <Descriptions.Item label="เลขธุรกรรม">{record.ts_id || "-"}</Descriptions.Item>
        <Descriptions.Item label="ผู้ถือเคสตอนนี้">{record.assign_to_name || "-"}</Descriptions.Item>
        <Descriptions.Item label="ปิดงานโดย">{record.finish_by_name || "-"}</Descriptions.Item>
        {showOriginalNote && (
          <Descriptions.Item label="Note ตอนสร้างเคส" span={2}>{textBlock(originalNote)}</Descriptions.Item>
        )}
        <Descriptions.Item label="Note" span={2}>
          {textBlock(record.note)}
        </Descriptions.Item>
        {closed && (
          <Descriptions.Item label="เหตุผลที่ปิดเคส" span={2}>{textBlock(cancelReason)}</Descriptions.Item>
        )}
      </Descriptions>

      {/* ── ไทม์ไลน์ ── */}
      <Title level={4} style={{ marginTop: 24 }}>ไทม์ไลน์</Title>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="สร้างเคส">
          {record.create_by_name || createdLog?.by || "-"} · {createdLog?.date || formatDate(record.createdate)}
        </Descriptions.Item>
        <Descriptions.Item label="เริ่มทำครั้งแรก">{startLog ? who(startLog) : record.start_by_name || "-"}</Descriptions.Item>
        {finishLog && <Descriptions.Item label="ทำสำเร็จ">{who(finishLog)}</Descriptions.Item>}
        {rejectLog && <Descriptions.Item label="ตีกลับล่าสุด">{who(rejectLog)}</Descriptions.Item>}
        {informLog && <Descriptions.Item label="ขอข้อมูลล่าสุด">{who(informLog)}</Descriptions.Item>}
        {cancelLog && <Descriptions.Item label="ปิดเคส (Cancel)">{who(cancelLog)}</Descriptions.Item>}
        <Descriptions.Item label="สร้าง → เริ่มทำ">{formatDuration(createdAt, startAt) || "-"}</Descriptions.Item>
        <Descriptions.Item label="เริ่มทำ → จบ">{formatDuration(startAt, endAt) || "-"}</Descriptions.Item>
        <Descriptions.Item label="สร้าง → จบ">{formatDuration(createdAt, endAt) || "-"}</Descriptions.Item>
        <Descriptions.Item label="จำนวนครั้ง">
          <Space wrap size={4}>
            <Tag>ตีกลับ {countLog("Reject")}</Tag>
            <Tag>ขอข้อมูล {countLog("Inform")}</Tag>
            <Tag>แก้/เติมส่งกลับ {countLog("RejectFix") + countLog("InformFix")}</Tag>
            <Tag>พักเคส {countLog("Pending")}</Tag>
          </Space>
        </Descriptions.Item>
      </Descriptions>

      {/* ── หลักฐานการตีกลับ / ขอข้อมูลเพิ่ม (แสดงทุกสถานะ ถ้ามีข้อมูล) ── */}
      {(hasReject || hasInform) && (
        <>
          <Title level={4} style={{ marginTop: 24 }}>หลักฐานการตีกลับ / ขอข้อมูลเพิ่ม</Title>
          <Descriptions bordered size="small" column={1}>
            {hasReject && (
              <Descriptions.Item label="เหตุผลที่ตีกลับ">{textBlock(record.reject_remark)}</Descriptions.Item>
            )}
            {hasReject && (
              <Descriptions.Item label="ตีกลับโดย">{record.rejectByUserName || rejectLog?.by || "-"}</Descriptions.Item>
            )}
            {hasInform && (
              <Descriptions.Item label="ข้อมูลที่ขอเพิ่ม">{textBlock(record.inform_remark)}</Descriptions.Item>
            )}
            {hasInform && (
              <Descriptions.Item label="ขอข้อมูลโดย">{record.inform_by_name || informLog?.by || "-"}</Descriptions.Item>
            )}
          </Descriptions>
          <div style={{ marginTop: 16 }}>
            {renderImages(record.imgReject, "รูปหลักฐานการตีกลับ")}
            {renderImages(record.imgInform, "รูปหลักฐานการขอข้อมูลเพิ่ม")}
          </div>
          {countLog("Reject") + countLog("Inform") > 1 && (
            <Text type="secondary">มีการตีกลับ/ขอข้อมูลมากกว่า 1 รอบ — ดูเหตุผลและรูปของแต่ละรอบในประวัติด้านล่าง</Text>
          )}
        </>
      )}

      <Divider />

      {/* ── รูปภาพของเคส ── */}
      {renderImages(record.imgRecipe, "รูปภาพ Recipe")}
      {renderImages(record.imgChoice, "รูปภาพ Choice")}

      {record.pending_log && record.pending_log.length > 0 && (
        <>
          <Title level={4}>Pending Log</Title>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 24 }}>
            <thead>
              <tr>
                <th style={{ border: "1px solid #f0f0f0", padding: 8, background: "#fafafa", width: "12%" }}>วันที่</th>
                <th style={{ border: "1px solid #f0f0f0", padding: 8, background: "#fafafa", width: "37%" }}>Remark</th>
                <th style={{ border: "1px solid #f0f0f0", padding: 8, background: "#fafafa", width: "11%" }}>By</th>
              </tr>
            </thead>
            <tbody>
              {record.pending_log.map((log, index) => (
                <tr key={index}>
                  <td style={{ border: "1px solid #f0f0f0", padding: 8 }}>{log.date}</td>
                  <td style={{ border: "1px solid #f0f0f0", padding: 8 }}>{log.remark}</td>
                  <td style={{ border: "1px solid #f0f0f0", padding: 8 }}>{log.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ── ข้อมูล Data Manage ── */}
      {record.dataManage && (
        <>
          <Title level={4}>Case Manage</Title>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="User ID">{record.dataManage.userName}</Descriptions.Item>
            <Descriptions.Item label="Case 1">{record.dataManage.case1Name}</Descriptions.Item>
            <Descriptions.Item label="Case 2">{record.dataManage.case2Name}</Descriptions.Item>
            <Descriptions.Item label="Case 3">{record.dataManage.case3Name}</Descriptions.Item>
            {(record.date_Deposit || record.time_Deposit) && (
              <Descriptions.Item label="วันที่/เวลาฝาก" span={2}>
                {[record.date_Deposit ? dayjs(record.date_Deposit).format("DD/MM/YYYY") : null, record.time_Deposit]
                  .filter(Boolean)
                  .join(" ")}
              </Descriptions.Item>
            )}
          </Descriptions>

          {record.dataManage.customFields && (
            <>
              <Title level={5} style={{ marginTop: 16, marginBottom: 16 }}>
                Custom Fields
              </Title>
              {renderCustomFields(record.dataManage.customFields)}
            </>
          )}

          <Divider />
        </>
      )}

      {/* ── ประวัติทั้งหมด (รวมรูปหลักฐานของทุกรอบ) ── */}
      <Title level={4}>ประวัติเคส</Title>
      {logs.length > 0 ? (
        <Table
          rowKey="_key"
          columns={historyColumns}
          dataSource={logs}
          pagination={false}
          size="small"
          bordered
          scroll={{ x: "max-content" }}
        />
      ) : (
        <Empty description="ไม่มีประวัติ" />
      )}
    </>
  );
}

export const caseStatusMeta = (status) => STATUS_META[status] || { color: "default", label: status };

const CaseDetailsModal = ({ open, onClose, caseId, record = null, footer = null, extra = null, width = 1000 }) => {
  const { data, isFetching, isError, error } = useQuery(
    ["caseDetail", caseId],
    async () => {
      const { result } = await GET(`/api/v1/caseDetail/${caseId}`);
      return result;
    },
    // เปิดทีไรดึงใหม่ทีนั้น — รายละเอียดต้องเป็นสถานะล่าสุด ไม่ใช่ของที่ค้างใน cache
    { enabled: Boolean(open && caseId), staleTime: 0, cacheTime: 60 * 1000, retry: 0, refetchOnWindowFocus: false }
  );

  const shown = (caseId && data && String(data.id) === String(caseId) ? data : null) || record;
  const statusMeta = shown ? caseStatusMeta(shown.status) : null;

  return (
    <Modal
      title={
        <Space wrap>
          <span>{caseId ? `รายละเอียดเคส #${caseId}` : "รายละเอียด"}</span>
          {statusMeta && caseId && <Tag color={statusMeta.color}>{statusMeta.label}</Tag>}
          {shown?.caseName && caseId && <Tag>{shown.caseName}</Tag>}
          {isFetching && <Spin size="small" />}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      width={width}
      style={{ top: 20 }}
      styles={{ body: { maxHeight: "80vh", overflowY: "auto" } }}
      destroyOnClose
    >
      {extra}
      {!caseId ? (
        <Empty style={{ margin: "24px 0" }} description="รายการนี้ยังไม่มีเคสในระบบผูกอยู่" />
      ) : (
        <>
          {isError && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={error?.response?.data?.message || "โหลดรายละเอียดล่าสุดไม่สำเร็จ"}
              description={record ? "กำลังแสดงข้อมูลจากรายการแทน อาจไม่ครบทุกช่อง" : null}
            />
          )}
          {shown ? (
            <CaseDetailsBody record={shown} />
          ) : (
            <div style={{ textAlign: "center", padding: 48 }}>
              <Spin />
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default CaseDetailsModal;
